/* eslint-disable max-lines */
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import * as yup from "yup";
import FormWrapper from "../../../../components/ui/FormWrapper";
import Input from "../../../../components/ui/input/Input";
import Select from "../../../../components/ui/select/Select";
import SpinnerLoader from "../../../../components/ui/SpinnerLoader";
import {
  GTFSBlockedEntityType,
  IGTFSBlockForm,
  IGTFSBlockTypeOption
} from "../../../../interfaces/endpoints/IGTFS.types";
import { addGTFSBlock } from "../../../../services/GTFS.service";

const ENTITY_TYPE_OPTIONS: IGTFSBlockTypeOption[] = [
  { value: GTFSBlockedEntityType.Operator, label: "Operatori" },
  { value: GTFSBlockedEntityType.Line, label: "Linja" },
  { value: GTFSBlockedEntityType.Trip, label: "Orari (Trip)" }
];

const schema = yup.object().shape({
  entityType: yup
    .object()
    .shape({ value: yup.mixed().required("Lloji është i detyrueshëm") })
    .required("Lloji është i detyrueshëm"),
  entityId: yup
    .number()
    .typeError("Entiteti ID është i detyrueshëm")
    .required("Entiteti ID është i detyrueshëm")
    .integer("Vlera duhet të jetë numër i plotë")
    .positive("Vlera duhet të jetë pozitive"),
  reason: yup.string().max(500, "Arsyeja nuk mund të kalojë 500 karaktere")
});

const CreateGTFSBlock: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitSuccessful, dirtyFields }
  } = useForm<IGTFSBlockForm>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      entityType: undefined,
      entityId: undefined,
      reason: ""
    }
  });

  const onSubmit = async (data: IGTFSBlockForm) => {
    setIsSubmitting(true);
    try {
      if (!data.entityType?.value) {
        throw new Error("Lloji është i detyrueshëm");
      }

      const response = await addGTFSBlock({
        entityType: data.entityType.value,
        entityId: Number(data.entityId),
        reason: data.reason?.trim() || undefined
      });

      if (response.status !== 200 && response.status !== 201) {
        toast.error((response as any).message, { duration: 3500 });
        return;
      }

      toast.success("Entiteti u bllokua me sukses");
      setTimeout(() => {
        navigate("/gtfs/blocks", { state: ["Menaxhimi i GTFS", "Bllokimet"] });
      }, 500);
    } catch (error) {
      toast.error("Bllokimi dështoi. Ju lutem kontrolloni të dhënat dhe provoni përsëri.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Link
        to="/gtfs/blocks"
        state={["Menaxhimi i GTFS", "Bllokimet"]}
        className="flex items-center mb-4 text-primary"
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        Kthehu tek lista
      </Link>
      <FormWrapper
        showPrompt={!isSubmitSuccessful && !!Object.keys(dirtyFields).length}
        title="Blloko entitet nga GTFS"
        onSubmit={handleSubmit(onSubmit)}
      >
        {isSubmitting ? (
          <div className="flex justify-center items-center py-8">
            <SpinnerLoader />
          </div>
        ) : (
          <div className="grid gap-6">
            <Controller
              control={control}
              name="entityType"
              render={({ field: { onChange, value } }) => (
                <Select
                  label="Lloji i entitetit*"
                  placeholder="Zgjedh llojin"
                  options={ENTITY_TYPE_OPTIONS}
                  selectedItem={value as IGTFSBlockTypeOption}
                  onSelectedItemChange={(item) => onChange(item)}
                  getOptionLabel={(o) => <span>{o.label}</span>}
                  itemToString={(o) => o?.label || ""}
                  error={errors.entityType?.message}
                />
              )}
            />
            <Input
              label="Entiteti ID*"
              type="number"
              placeholder="Shkruani ID-në e operatorit, linjës ose orarit"
              error={errors.entityId?.message}
              {...register("entityId")}
            />
            <Input
              label="Arsyeja"
              placeholder="Arsyeja e bllokimit (opsionale)"
              error={errors.reason?.message}
              {...register("reason")}
            />
          </div>
        )}
      </FormWrapper>
    </>
  );
};

export default CreateGTFSBlock;
